import { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DollarSign,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Package,
  CalendarIcon,
  Download,
  FileText,
  BarChart3,
} from "lucide-react";
import { format, subDays, startOfWeek, startOfMonth, startOfQuarter, startOfYear, endOfDay } from "date-fns";
import { cn } from "@/lib/utils";
import {
  useGetSalesOverviewQuery,
  useGetRevenueDataQuery,
  useGetPopularItemsQuery,
  useGetCategorySalesQuery,
  useGetOrderStatusDistributionQuery,
  useLazyGetExportDataQuery,
  type GroupBy,
} from "@/store/analyticsApi";
import {
  RevenueLineChart,
  PopularItemsChart,
  CategoryPieChart,
  OrderStatusChart,
} from "@/components/admin/charts";
import { toast } from "sonner";
import { generateCSV, generatePDF } from "@/lib/exportUtils";

// Date range presets
type DatePreset =
  | "today"
  | "yesterday"
  | "last7days"
  | "last30days"
  | "last90days"
  | "thisWeek"
  | "thisMonth"
  | "thisQuarter"
  | "thisYear"
  | "ytd"
  | "custom";

interface DateRange {
  startDate: Date;
  endDate: Date;
}

const presetOptions: { value: DatePreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7days", label: "Last 7 Days" },
  { value: "last30days", label: "Last 30 Days" },
  { value: "last90days", label: "Last 90 Days" },
  { value: "thisWeek", label: "This Week" },
  { value: "thisMonth", label: "This Month" },
  { value: "thisQuarter", label: "This Quarter" },
  { value: "thisYear", label: "This Year" },
  { value: "ytd", label: "Year to Date" },
  { value: "custom", label: "Custom Range" },
];

const getDateRangeFromPreset = (preset: DatePreset): DateRange => {
  const now = new Date();
  const today = endOfDay(now);

  switch (preset) {
    case "today":
      return {
        startDate: new Date(now.setHours(0, 0, 0, 0)),
        endDate: today,
      };
    case "yesterday":
      const yesterday = subDays(now, 1);
      return {
        startDate: new Date(yesterday.setHours(0, 0, 0, 0)),
        endDate: new Date(new Date(yesterday).setHours(23, 59, 59, 999)),
      };
    case "last7days":
      return {
        startDate: subDays(today, 6),
        endDate: today,
      };
    case "last30days":
      return {
        startDate: subDays(today, 29),
        endDate: today,
      };
    case "last90days":
      return {
        startDate: subDays(today, 89),
        endDate: today,
      };
    case "thisWeek":
      return {
        startDate: startOfWeek(now, { weekStartsOn: 1 }),
        endDate: today,
      };
    case "thisMonth":
      return {
        startDate: startOfMonth(now),
        endDate: today,
      };
    case "thisQuarter":
      return {
        startDate: startOfQuarter(now),
        endDate: today,
      };
    case "thisYear":
      return {
        startDate: startOfYear(now),
        endDate: today,
      };
    case "ytd":
      return {
        startDate: startOfYear(now),
        endDate: today,
      };
    default:
      return {
        startDate: subDays(today, 29),
        endDate: today,
      };
  }
};

const formatCurrency = (value: number) => {
  return `₦${value.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export function AnalyticsScreen() {
  const [datePreset, setDatePreset] = useState<DatePreset>("last30days");
  const [customDateRange, setCustomDateRange] = useState<DateRange | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>("day");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Get current date range
  const dateRange = useMemo(() => {
    if (datePreset === "custom" && customDateRange) {
      return customDateRange;
    }
    return getDateRangeFromPreset(datePreset);
  }, [datePreset, customDateRange]);

  // Format dates for API
  const apiDateParams = useMemo(
    () => ({
      startDate: format(dateRange.startDate, "yyyy-MM-dd"),
      endDate: format(dateRange.endDate, "yyyy-MM-dd"),
    }),
    [dateRange]
  );

  // Fetch data
  const { data: overviewData, isLoading: overviewLoading } =
    useGetSalesOverviewQuery(apiDateParams);
  const { data: revenueData, isLoading: revenueLoading } = useGetRevenueDataQuery({
    ...apiDateParams,
    groupBy,
  });
  const { data: popularItemsData, isLoading: popularItemsLoading } =
    useGetPopularItemsQuery({ ...apiDateParams, limit: 10 });
  const { data: categorySalesData, isLoading: categorySalesLoading } =
    useGetCategorySalesQuery(apiDateParams);
  const { data: orderStatusData, isLoading: orderStatusLoading } =
    useGetOrderStatusDistributionQuery(apiDateParams);

  // Export functionality
  const [triggerExport, { isLoading: exportLoading }] = useLazyGetExportDataQuery();

  const handleExportCSV = async () => {
    try {
      const result = await triggerExport(apiDateParams).unwrap();
      if (result.data) {
        generateCSV(result.data);
        toast.success("CSV report downloaded successfully");
      }
    } catch (error) {
      toast.error("Failed to generate CSV report");
    }
  };

  const handleExportPDF = async () => {
    try {
      const result = await triggerExport(apiDateParams).unwrap();
      if (result.data) {
        generatePDF(result.data);
        toast.success("PDF report downloaded successfully");
      }
    } catch (error) {
      toast.error("Failed to generate PDF report");
    }
  };

  const overview = overviewData?.data;

  // Handle custom date selection
  const handleDateSelect = (range: { from?: Date; to?: Date } | undefined) => {
    if (range?.from && range?.to) {
      setCustomDateRange({
        startDate: range.from,
        endDate: range.to,
      });
      setDatePreset("custom");
      setIsCalendarOpen(false);
    }
  };

  // Auto-adjust groupBy based on date range
  useMemo(() => {
    const days = Math.ceil(
      (dateRange.endDate.getTime() - dateRange.startDate.getTime()) /
        (1000 * 60 * 60 * 24)
    );
    if (days <= 14) {
      setGroupBy("day");
    } else if (days <= 90) {
      setGroupBy("week");
    } else {
      setGroupBy("month");
    }
  }, [dateRange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="h-8 w-8" />
            Analytics
          </h1>
          <p className="text-muted-foreground">
            Sales reports, revenue charts, and business insights
          </p>
        </div>

        {/* Export Buttons */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportCSV}
            disabled={exportLoading}
          >
            <FileText className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPDF}
            disabled={exportLoading}
          >
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">
                Date Range
              </label>
              <div className="flex gap-2 flex-wrap">
                <Select
                  value={datePreset}
                  onValueChange={(value) => setDatePreset(value as DatePreset)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    {presetOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "justify-start text-left font-normal min-w-[240px]",
                        !customDateRange && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(dateRange.startDate, "MMM d, yyyy")} -{" "}
                      {format(dateRange.endDate, "MMM d, yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      initialFocus
                      mode="range"
                      defaultMonth={dateRange.startDate}
                      selected={{
                        from: dateRange.startDate,
                        to: dateRange.endDate,
                      }}
                      onSelect={handleDateSelect}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Group By</label>
              <Select
                value={groupBy}
                onValueChange={(value) => setGroupBy(value as GroupBy)}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Revenue */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <div className="animate-pulse h-8 bg-muted rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {formatCurrency(overview?.totalRevenue || 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  {overview?.previousPeriod.revenueChange !== undefined && (
                    <>
                      {overview.previousPeriod.revenueChange >= 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-500" />
                      )}
                      <span
                        className={
                          overview.previousPeriod.revenueChange >= 0
                            ? "text-green-500"
                            : "text-red-500"
                        }
                      >
                        {overview.previousPeriod.revenueChange >= 0 ? "+" : ""}
                        {overview.previousPeriod.revenueChange.toFixed(1)}%
                      </span>
                      <span>vs previous period</span>
                    </>
                  )}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Total Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <div className="animate-pulse h-8 bg-muted rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {overview?.totalOrders || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  {overview?.previousPeriod.ordersChange !== undefined && (
                    <>
                      {overview.previousPeriod.ordersChange >= 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-500" />
                      )}
                      <span
                        className={
                          overview.previousPeriod.ordersChange >= 0
                            ? "text-green-500"
                            : "text-red-500"
                        }
                      >
                        {overview.previousPeriod.ordersChange >= 0 ? "+" : ""}
                        {overview.previousPeriod.ordersChange.toFixed(1)}%
                      </span>
                      <span>vs previous period</span>
                    </>
                  )}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Average Order Value */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Avg Order Value
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <div className="animate-pulse h-8 bg-muted rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {formatCurrency(overview?.averageOrderValue || 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Per successful order
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Completed Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Completed Orders
            </CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {overviewLoading ? (
              <div className="animate-pulse h-8 bg-muted rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {overview?.completedOrders || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {overview?.pendingOrders || 0} pending,{" "}
                  {overview?.cancelledOrders || 0} cancelled
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      <RevenueLineChart
        data={revenueData?.data || []}
        isLoading={revenueLoading}
        title="Revenue Over Time"
      />

      {/* Charts Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Popular Items */}
        <PopularItemsChart
          data={popularItemsData?.data || []}
          isLoading={popularItemsLoading}
          title="Top Selling Items"
          maxItems={10}
        />

        {/* Category Sales */}
        <CategoryPieChart
          data={categorySalesData?.data || []}
          isLoading={categorySalesLoading}
          title="Sales by Category"
        />
      </div>

      {/* Order Status */}
      <div className="grid gap-6 lg:grid-cols-2">
        <OrderStatusChart
          data={orderStatusData?.data || []}
          isLoading={orderStatusLoading}
          title="Order Status Distribution"
        />

        {/* Popular Items Table */}
        <Card>
          <CardHeader>
            <CardTitle>Popular Items Details</CardTitle>
            <CardDescription>
              Detailed breakdown of top selling products
            </CardDescription>
          </CardHeader>
          <CardContent>
            {popularItemsLoading ? (
              <div className="animate-pulse space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-10 bg-muted rounded" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Qty Sold</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {popularItemsData?.data?.slice(0, 10).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-8 h-8 rounded object-cover"
                            />
                            <div>
                              <p className="font-medium text-sm truncate max-w-[150px]">
                                {item.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {item.category}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {item.quantitySold}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.revenue)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!popularItemsData?.data ||
                      popularItemsData.data.length === 0) && (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="text-center text-muted-foreground"
                        >
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
